import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  createDesignation,
  deleteDesignation,
  listDesignations,
  syncDesignationsFromApi,
  updateDesignation,
  type DesignationDraft,
  type DesignationRecord,
} from "@/lib/hr/designationsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { Briefcase, CalendarIcon, Clock, Edit, Eye, FileText, Filter, LayoutGrid, List, Loader2, Plus, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatDesigDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type DesignationSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

function designationErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

const Designations = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "designation_can_view");
  const canCreate = can(user, "designation_can_create");
  const canUpdate = can(user, "designation_can_update");
  const canDelete = can(user, "designation_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<DesignationSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterDescription, setFilterDescription] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<DesignationRecord | null>(null);
  const [draft, setDraft] = useState<DesignationDraft>({ name: "", description: "" });
  const [deleteTarget, setDeleteTarget] = useState<DesignationRecord | null>(null);
  const [viewDesig, setViewDesig] = useState<DesignationRecord | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const designations = useMemo(() => listDesignations(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncDesignationsFromApi();
        if (mounted) refresh();
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Designations] Load failed", error);
        if (mounted) toast.error("Could not fetch designations. Showing cached data if any.");
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return designations.filter((d) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(d.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(d.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const nameNeedle = filterName.trim().toLowerCase();
      if (nameNeedle && !d.name.toLowerCase().includes(nameNeedle)) return false;
      const descNeedle = filterDescription.trim().toLowerCase();
      if (descNeedle && !d.description.toLowerCase().includes(descNeedle)) return false;
      const matchesQuery =
        !q ||
        d.name.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q);
      return matchesQuery;
    });
  }, [designations, query, filterCreatedDate, quickDate, filterName, filterDescription]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) + (filterName.trim() ? 1 : 0) + (filterDescription.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterDescription("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterDescription}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft({ name: "", description: "" });
    setModalOpen(true);
  };

  const openEdit = (d: DesignationRecord) => {
    setEditing(d);
    setDraft({ name: d.name, description: d.description });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft({ name: "", description: "" });
  };

  const saveDesignation = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Designation name is required.");
      return;
    }
    setIsSaving(true);
    try {
      const payload: DesignationDraft = {
        name: draft.name.trim(),
        description: draft.description?.trim() ?? "",
      };
      if (editing) {
        const result = await updateDesignation(editing.id, payload);
        if (!result) {
          toast.error("Designation not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createDesignation(payload);
        toast.success(result.message);
      }
      closeModal();
      refresh();
    } catch (error) {
      console.error("[Designations] Save failed", error);
      toast.error(designationErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteDesignation(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Designations] Delete failed", error);
      toast.error(designationErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const descPreview = (text: string, max = 80) => {
    const t = text.trim();
    if (!t) return "—";
    return t.length > max ? `${t.slice(0, max)}…` : t;
  };

  return isForbidden ? (
    <PermissionDenied
      title="Designations access required"
      message="You don’t have permission to view designations. Ask an administrator to grant designation_can_view_any."
    />
  ) : (
    <Layout title="Designations">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Designations</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Job titles and roles for employees.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add Designation
            </Button>
          ) : null}
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit designation" : "New designation"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Designation name <span className="text-destructive">*</span>
                </Label>
                <Input
                  className="glass-input h-10"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Senior Manager"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  placeholder="Optional short description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void saveDesignation()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewDesig} onOpenChange={(o) => !o && setViewDesig(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Designation details</DialogTitle>
            </DialogHeader>
            {viewDesig ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Name</dt>
                  <dd className="font-medium text-foreground">{viewDesig.name}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Description</dt>
                  <dd className="text-muted-foreground whitespace-pre-wrap">{viewDesig.description?.trim() || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatDesigDate(viewDesig.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Last updated</dt>
                  <dd className="text-muted-foreground">{formatDesigDate(viewDesig.updatedAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewDesig(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or description…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter designations</SheetTitle>
                  <SheetDescription>Refine by name, description, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Designation</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Manager"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Description contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterDescription}
                        onChange={(e) => setFilterDescription(e.target.value)}
                        placeholder="e.g. sales"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="desig-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as DesignationSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="desig-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as DesignationSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading designations…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {designations.length === 0
              ? canCreate
                ? "No designations yet. Click “Add Designation” to create one."
                : "No designations yet."
              : "No designations match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <CatalogEntityCard
                  title={d.name}
                  primaryMeta={{
                    label: "Description",
                    value: descPreview(d.description, 60),
                    icon: FileText,
                    iconClassName: "text-red-600",
                  }}
                  secondaryMeta={{
                    label: "Updated",
                    value: formatCatalogAuditDate(d.updatedAt),
                    icon: Clock,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`Created: ${formatCatalogAuditDate(d.createdAt)}`}
                  onView={canView ? () => setViewDesig(d) : undefined}
                  onEdit={canUpdate ? () => openEdit(d) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(d) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((d, i) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Briefcase className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{d.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{descPreview(d.description, 120)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(d.updatedAt)}</p>
                  </div>
                  <div className="text-right ml-auto">
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(d.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewDesig(d)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(d)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(d)}
                        aria-label="Delete designation"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this designation?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Designations;
